import * as React from 'react';
import { uiMarkerInternal } from '../recipes/shared';
import { cn } from '@relay-towing/tokens';
import { Text } from './Text';
import { TableCell, type TableCellProps } from './Table';
import { formatPhoneNumber } from './PhoneInput';

export type ValueFormat =
  | 'text'
  | 'date'
  | 'datetime'
  | 'currency'
  | 'phone'
  | 'vin';

/** Formats that should render in a monospaced face so characters line up. */
const MONO_FORMATS: ValueFormat[] = ['vin'];
/** Formats that are numbers and should be right-aligned in a table. */
const NUMERIC_FORMATS: ValueFormat[] = ['currency'];

/**
 * Renders one value in the product's house style.
 *
 * Replaces the app's `enhanced-table-cell`, with three fixes:
 *
 *   • Currency uses Intl, so it gets thousands separators. The original used
 *     `toFixed(2)`, which rendered $8006.10 instead of $8,006.10.
 *   • Invalid dates render as the empty placeholder rather than the string
 *     "Invalid Date".
 *   • Phone numbers are actually formatted; the original had a `phone` case
 *     that returned the value untouched with a TODO.
 */
export function formatValue(
  value: unknown,
  format: ValueFormat = 'text'
): string {
  if (value === null || value === undefined || value === '') return '';

  switch (format) {
    case 'date':
    case 'datetime': {
      const date = new Date(value as string);
      if (Number.isNaN(date.getTime())) return '';
      return format === 'date'
        ? date.toLocaleDateString()
        : date.toLocaleString();
    }
    case 'currency': {
      const amount = Number(value);
      if (Number.isNaN(amount)) return '';
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
      }).format(amount);
    }
    case 'phone':
      return formatPhoneNumber(String(value));
    case 'vin':
      return String(value).toUpperCase();
    default:
      return String(value);
  }
}

export interface DataValueProps
  extends Omit<React.HTMLAttributes<HTMLSpanElement>, 'children'> {
  value: unknown;
  format?: ValueFormat;
  /** Shown when the value is empty. */
  placeholder?: string;
}

/**
 * A formatted value, with an explicit empty state.
 *
 * The placeholder is deliberately styled as muted italic: "N/A" in the same
 * weight as real data reads as data.
 */
export const DataValue = React.forwardRef<HTMLSpanElement, DataValueProps>(
  function DataValue(
    { value, format = 'text', placeholder = 'N/A', className, ...props },
    ref
  ) {
    // React elements pass straight through — some cells render a badge or link.
    if (React.isValidElement(value)) return value;

    const formatted = formatValue(value, format);

    if (!formatted) {
      return (
        <span
          ref={ref}
          className={cn('italic text-muted-foreground', className)}
          {...props}
        >
          {placeholder}
        </span>
      );
    }

    return (
      <span
        ref={ref}
        className={cn(
          MONO_FORMATS.includes(format) && 'font-mono',
          NUMERIC_FORMATS.includes(format) && 'tabular-nums',
          className
        )}
        {...props}
      >
        {formatted}
      </span>
    );
  }
);

export interface ValueCellProps
  extends Omit<TableCellProps, 'children' | 'numeric'> {
  value: unknown;
  format?: ValueFormat;
  placeholder?: string;
}

/**
 * A table cell that formats its value.
 *
 * Currency right-aligns automatically, so a money column lines up without the
 * caller having to remember `numeric`.
 */
export const ValueCell = React.forwardRef<HTMLTableCellElement, ValueCellProps>(
  function ValueCell({ value, format = 'text', placeholder, ...props }, ref) {
    return (
      <TableCell
        ref={ref}
        numeric={NUMERIC_FORMATS.includes(format)}
        {...props}
      >
        <DataValue value={value} format={format} placeholder={placeholder} />
      </TableCell>
    );
  }
);

export interface StackedValueProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, 'children'> {
  /** The line that carries the weight — a name, a date, an invoice number. */
  value: unknown;
  /** The quieter second line — a phone number, a time, a reference. */
  detail: unknown;
  format?: ValueFormat;
  detailFormat?: ValueFormat;
  placeholder?: string;
  /**
   * The two lines often mean different things when empty — a missing driver is
   * "Unassigned", a missing phone is "N/A" — so the second line can say so.
   * Falls back to `placeholder` when not given.
   */
  detailPlaceholder?: string;
}

/**
 * Two lines in one cell: a primary value with a quieter one beneath it.
 *
 * Tables use this to fit a pair of related facts in a single column — driver
 * and phone, date and time, invoice and reference. The app hand-wrote it eight
 * times as `<div><div className="font-medium">…<div className="text-sm
 * text-muted-foreground">…`, which is three divs and two colour decisions per
 * cell, made independently each time.
 *
 * Both lines run through DataValue, so an absent one renders the same muted
 * italic placeholder as every other empty value rather than a bespoke
 * `<span className="italic">N/A</span>`.
 */
export const StackedValue = React.forwardRef<HTMLDivElement, StackedValueProps>(
  function StackedValue(
    {
      value,
      detail,
      format = 'text',
      detailFormat = 'text',
      placeholder,
      detailPlaceholder,
      className,
      ...props
    },
    ref
  ) {
    return (
      <div ref={ref} className={cn(uiMarkerInternal, className)} {...props}>
        <Text variant="body-sm-strong">
          <DataValue value={value} format={format} placeholder={placeholder} />
        </Text>
        <Text variant="body-sm" tone="muted">
          <DataValue
            value={detail}
            format={detailFormat}
            placeholder={detailPlaceholder ?? placeholder}
          />
        </Text>
      </div>
    );
  }
);

export interface FieldValueProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, 'children'> {
  label: React.ReactNode;
  value: unknown;
  format?: ValueFormat;
  placeholder?: string;
}

/**
 * A label-over-value pair — the read-only counterpart to `Field`, and the
 * layout every detail panel and mobile card uses.
 */
export const FieldValue = React.forwardRef<HTMLDivElement, FieldValueProps>(
  function FieldValue(
    { label, value, format = 'text', placeholder, className, ...props },
    ref
  ) {
    return (
      <div
        ref={ref}
        className={cn(uiMarkerInternal, 'min-w-0 space-y-1', className)}
        {...props}
      >
        {/*
         * Overline rather than caption: uppercase micro-labels are what the
         * product already uses above a value — 49 field labels against 11 —
         * and it keeps a read-only pair at the same rank as the editable
         * fields beside it on the impound detail tab.
         */}
        <Text variant="overline" tone="muted">
          {label}
        </Text>
        <Text variant="body-sm-strong" as="div">
          <DataValue value={value} format={format} placeholder={placeholder} />
        </Text>
      </div>
    );
  }
);
